import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import AiSearch from './AiSearch';
import useAuth from '../auth/useAuth';
import useNarrowScreen from '../utils/useNarrowScreen';


const SIDEBAR_KEY = 'disiSidebarClosed';

const AiSearchContext = createContext(() => {});

/**
 * Opens the knowledge assistant sheet, optionally with a query already typed
 * in - for a page's own assistant field (the home page's) to hand over to the
 * sheet, so typing happens in one fixed place. Needs `showAiSearch`.
 */
export function useOpenAiSearch() {
  return useContext(AiSearchContext);
}

function readStoredSidebarState() {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === 'true';
  } catch {
    return false;
  }
}

// `preferences` and `user` used to be props here, but no caller ever supplied
// them: MainWindow passes the valueless attributes `preferences` / `user`,
// which JSX turns into the boolean `true`, and every other call site omits
// them entirely. So `user?.rights` was always undefined and the sidebar never
// offered "My Articles" or the create links to anyone, whatever their rights.
// Read them from the auth context instead, as the commented-out line intended.
// `sidebarClosed`: a page that needs the width (a plugin's editor, an article
// that is a process) closes the sidebar, whatever was remembered; opening it
// there is for the moment and not remembered either. It may turn true later -
// an article knows it is a process once the plugins have loaded - and when it
// turns false again the remembered state comes back.
function PageLayout({ children, showSearch = false, showAiSearch = false, maxWidth, sidebarClosed = false }) {
  const { preferences, user } = useAuth();

  // Default true (matches the DB column default) while preferences are
  // still loading, so the sidebar doesn't flash open before settling.
  const rememberSidebar = preferences?.rememberSidebar ?? true;
 
  // `rightsAnywhere`, not `rights`: an author of one department may write, and
  // the endpoint agrees - it asks the same question. `rights` is the
  // platform-wide set and gates the admin panel alone.
  const canManageArticles =
    user?.rightsAnywhere?.includes('articles.manage') ?? false;

  const canManageNews =
    user?.rightsAnywhere?.includes('news.manage') ?? false;

  const [isSidebarClosed, setSidebarClosed] = useState(() =>
    sidebarClosed || (rememberSidebar ? readStoredSidebarState() : false),
  );

  const wasSidebarClosed = useRef(sidebarClosed);
  useEffect(() => {
    if (sidebarClosed === wasSidebarClosed.current) return;
    wasSidebarClosed.current = sidebarClosed;
    setSidebarClosed(sidebarClosed || (rememberSidebar ? readStoredSidebarState() : false));
  }, [sidebarClosed, rememberSidebar]);

  // On a phone the open sidebar would leave the page a sliver beside it, so
  // there it starts as the icon rail and opens over the page for a moment -
  // never remembered, and the state above stays the wider screen's own.
  const isNarrow = useNarrowScreen();
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const { pathname } = useLocation();
  const closeOverlay = useCallback(() => setOverlayOpen(false), []);

  useEffect(() => { setOverlayOpen(false); }, [isNarrow, pathname]);

  useEffect(() => {
    if (!isNarrow || !isOverlayOpen) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setOverlayOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isNarrow, isOverlayOpen]);

  const isClosed = isNarrow ? !isOverlayOpen : isSidebarClosed;

  const [isAiOpen, setAiOpen] = useState(false);
  // Bumped on every hand-over, so the same text typed twice still arrives.
  const [aiRequest, setAiRequest] = useState({ query: '', seq: 0 });

  const openAi = useCallback((query = '') => {
    setAiRequest((current) => ({ query, seq: current.seq + 1 }));
    setAiOpen(true);
  }, []);

  const toggleSidebar = useCallback(() => {
    if (isNarrow) {
      setOverlayOpen((current) => !current);
      return;
    }
    setSidebarClosed((current) => {
      const next = !current;

      if (rememberSidebar && !sidebarClosed) {
        try { localStorage.setItem(SIDEBAR_KEY, String(next)); } catch { }
      }

      return next;
    });
  }, [isNarrow, rememberSidebar, sidebarClosed]);

  const closeAi = useCallback(() => setAiOpen(false), []);

  const contextValue = useMemo(() => openAi, [openAi]);

  return (
    <AiSearchContext.Provider value={contextValue}>
      <Header showSearch={showSearch} onOpenAi={() => openAi()} />
      <div className={`layout${isClosed ? ' sidebar-closed' : ''}${isNarrow && isOverlayOpen ? ' sidebar-overlay' : ''}`}>
        {isNarrow && isOverlayOpen && (
          // A tap beside the open sidebar closes it; Escape does too, so the
          // backdrop needs no keyboard handling of its own.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div className="sidebar-backdrop" onClick={closeOverlay} />
        )}
        <Sidebar
          isClosed={isClosed}
          onToggle={toggleSidebar}
          onNavigate={closeOverlay}
          canManageArticles={canManageArticles}
          canManageNews={canManageNews} />
        <div
          className="page-content"
          style={maxWidth ? { maxWidth } : undefined}
        >
          {children}
        </div>
      </div>
      {showAiSearch && <AiSearch isOpen={isAiOpen} onClose={closeAi} request={aiRequest} />}
    </AiSearchContext.Provider>
  );
}

export default PageLayout;
